// SubSkuCard.js
import React from "react";
import {
  Grid,
  TextField,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import CustomTypography from "../../globalStyles/fonts";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import DimensionsAndWeightInputWithUnit from "../../components/dimension-input";
import { productService } from "../../utils/services/productService";
import buildQuery from "../../utils/query-builder";
import { countryService } from "../../utils/services/country.service";
import CurrencyTextField from "../currency-field/CurrencyTextField";

const SubSkuCard = ({
  subSku,
  variant,
  variantIndex,
  subIndex,
  selectedClient,
  handleRemoveSubSku,
  handleSubSkuChange,
  onChangeSubDimensionAndWeight,
  updateSubSku,
  handleProductUpdate,
  formErrors,
}) => {
  return (
    <div
      style={{
        marginTop: 16,
        padding: 16,
        border: "1px solid var(--border-color)",
        backgroundColor: "var(--bg-primary)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography fontWeight={500}>
          {subSku.title || `Sub-SKU ${subIndex + 1}`}
        </Typography>
        <IconButton onClick={() => handleRemoveSubSku(variantIndex, subIndex)}>
          <DeleteIcon />
        </IconButton>
      </div>
      <Divider />

      <Grid container spacing={2.5} mt={1}>
        {variant.isBundle && (
          <Grid item xs={12} sm={12}>
            <CustomTypography
              style={{ textAlign: "left", fontWeight: 400 }}
              fontSize={14}
            >
              Select Product
            </CustomTypography>
            <AutocompleteWithPagination
              hideLabel
              selectedClient={selectedClient}
              backgroundColor="var(--bg-paper)"
              selectedItem={subSku.product}
              customLabel="Product"
              onDataChange={(product) =>
                handleProductUpdate(variantIndex, subIndex, product)
              }
              width="100%"
              optionKey="name"
              apiEndPoint={(limit, skip, search, client) => {
                const query = buildQuery({
                  limit: limit,
                  skip: skip,
                });

                return productService.fetch(client, search, query);
              }}
              errorMessage={
                formErrors[`${variantIndex}_subSku_${subIndex}_product`]
              }
            />
          </Grid>
        )}

        <Grid item xs={12} sm={4}>
          <CustomTypography
            style={{ textAlign: "left", fontWeight: 400 }}
            fontSize={14}
          >
            Title <span style={{ color: "red" }}>*</span>
          </CustomTypography>
          <TextField
            sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
            type="text"
            size="small"
            placeholder="Enter Title"
            value={subSku.title}
            onChange={(e) =>
              handleSubSkuChange(
                variantIndex,
                subIndex,
                "title",
                e.target.value,
              )
            }
            fullWidth
            error={!!formErrors[`${variantIndex}_subSku_${subIndex}_title`]}
            helperText={
              formErrors[`${variantIndex}_subSku_${subIndex}_title`] || " "
            }
          />
        </Grid>

        {variant.isBundle ? (
          <Grid item xs={12} sm={4}>
            <CustomTypography
              style={{ textAlign: "left", fontWeight: 400 }}
              fontSize={14}
            >
              Select SKU
            </CustomTypography>
            <AutocompleteWithPagination
              hideLabel
              selectedClient={selectedClient}
              backgroundColor="var(--bg-paper)"
              customLabel="SKU"
              selectedItem={subSku.sku}
              onDataChange={(selectedSku) =>
                updateSubSku(variantIndex, subIndex, subSku, selectedSku)
              }
              width="100%"
              optionKey="sku"
              apiEndPoint={(_, __, search) => {
                const product = subSku?.product;
                if (product?.variants?.length > 0) {
                  const skus = product.variants
                    .map((variant) => variant.sku)
                    .filter((sku) => sku && sku.includes(search || ""));

                  return Promise.resolve({
                    data: skus.map((sku) => ({ sku })),
                    total: skus.length,
                  });
                }
                return Promise.resolve({
                  data: [],
                  total: 0,
                });
              }}
              errorMessage={
                formErrors[`${variantIndex}_subSku_${subIndex}_sku`]
              }
            />
          </Grid>
        ) : (
          <Grid item xs={12} sm={4}>
            <CustomTypography
              style={{ textAlign: "left", fontWeight: 400 }}
              fontSize={14}
            >
              SKU <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
              type="text"
              size="small"
              placeholder="Enter SKU"
              value={subSku.sku}
              onChange={(e) =>
                handleSubSkuChange(
                  variantIndex,
                  subIndex,
                  "sku",
                  e.target.value,
                )
              }
              fullWidth
              error={!!formErrors[`${variantIndex}_subSku_${subIndex}_sku`]}
              helperText={
                formErrors[`${variantIndex}_subSku_${subIndex}_sku`] || " "
              }
            />
          </Grid>
        )}

        <Grid item xs={12} sm={4}>
          <CustomTypography
            style={{ textAlign: "left", fontWeight: 400 }}
            fontSize={14}
          >
            Price (USD)
          </CustomTypography>
          <CurrencyTextField
            sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
            size="small"
            placeholder="Enter Price"
            value={subSku.price}
            onChange={(value) =>
              handleSubSkuChange(variantIndex, subIndex, "price", value)
            }
            fullWidth
            error={!!formErrors[`${variantIndex}_subSku_${subIndex}_price`]}
            helperText={
              formErrors[`${variantIndex}_subSku_${subIndex}_price`] || ""
            }
          />
        </Grid>

        {!variant.isMaster && (
          <>
            <Grid item xs={12} sm={4}>
              <CustomTypography
                style={{ textAlign: "left", fontWeight: 400 }}
                fontSize={14}
              >
                HS Code
              </CustomTypography>
              <TextField
                sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                size="small"
                placeholder="Enter HS Code"
                value={subSku.hs_code}
                onChange={(e) =>
                  handleSubSkuChange(
                    variantIndex,
                    subIndex,
                    "hs_code",
                    e.target.value,
                  )
                }
                fullWidth
                error={
                  !!formErrors[`${variantIndex}_subSku_${subIndex}_hs_code`]
                }
                helperText={
                  formErrors[`${variantIndex}_subSku_${subIndex}_hs_code`] ||
                  " "
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomTypography
                style={{ textAlign: "left", fontWeight: 400 }}
                fontSize={14}
              >
                HTS Code
              </CustomTypography>
              <TextField
                sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                size="small"
                placeholder="Enter HTS Code"
                value={subSku.hts_code}
                onChange={(e) =>
                  handleSubSkuChange(
                    variantIndex,
                    subIndex,
                    "hts_code",
                    e.target.value,
                  )
                }
                fullWidth
                error={
                  !!formErrors[`${variantIndex}_subSku_${subIndex}_hts_code`]
                }
                helperText={
                  formErrors[`${variantIndex}_subSku_${subIndex}_hts_code`] ||
                  " "
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomTypography
                style={{ textAlign: "left", fontWeight: 400 }}
                fontSize={14}
              >
                Country of Origin
              </CustomTypography>
              <AutocompleteWithPagination
                hideLabel
                selectedItem={
                  subSku.country_of_origin
                    ? {
                        code2: `${subSku?.country_of_origin}`,
                      }
                    : { code2: "" }
                }
                customLabel="Country of Origin"
                onDataChange={(value) =>
                  handleSubSkuChange(
                    variantIndex,
                    subIndex,
                    "country_of_origin",
                    value ? value.code2 : "",
                  )
                }
                width="100%"
                optionKey={(country) =>
                  `${country.code2} ${country.name ? `(${country.name})` : ""}`
                }
                apiEndPoint={(limit, skip, search = "") => {
                  const query = `$limit=${limit}&$skip=${skip}`;
                  return countryService.fetch(search, query, "code2");
                }}
                errorMessage={
                  formErrors[
                    `${variantIndex}_subSku_${subIndex}_country_of_origin`
                  ]
                }
              />
            </Grid>
            <Grid item xs={12}>
              <DimensionsAndWeightInputWithUnit
                dimensions={{
                  height: subSku.height,
                  length: subSku.length,
                  width: subSku.width,
                }}
                weight={subSku.weight}
                onChange={(e) =>
                  onChangeSubDimensionAndWeight(
                    variantIndex,
                    subIndex,
                    e.field,
                    e.value,
                  )
                }
                dimensionUnitValue={subSku.height_unit}
                weightUnitValue={subSku.weight_unit}
                error={{
                  height:
                    formErrors[`${variantIndex}_subSku_${subIndex}_height`] ||
                    "",
                  width:
                    formErrors[`${variantIndex}_subSku_${subIndex}_width`] ||
                    "",
                  length:
                    formErrors[`${variantIndex}_subSku_${subIndex}_length`] ||
                    "",
                  dimension_unit:
                    formErrors[
                      `${variantIndex}_subSku_${subIndex}_dimension_unit`
                    ] || "",
                  weight_unit:
                    formErrors[
                      `${variantIndex}_subSku_${subIndex}_weight_unit`
                    ] || "",
                  weight:
                    formErrors[`${variantIndex}_subSku_${subIndex}_weight`] ||
                    "",
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};

export default SubSkuCard;
